import { useCallback, useEffect, useMemo, useRef } from "react";
import { createEmptyBucket, getBucketKey } from "./reservationsBuckets";
import type {
  ReservationSearch,
  ReservationsBucket,
} from "./reservationsTypes";

type UseReservationsBucketsOptions = {
  applyBucketState: (bucket: ReservationsBucket) => void;
  clearReservationsCache: () => void;
  appliedSearchVariables: ReservationSearch;
};

/**
 * Manages bucketed reservation state keyed by search parameters.
 */
export default function useReservationsBuckets({
  applyBucketState,
  clearReservationsCache,
  appliedSearchVariables,
}: UseReservationsBucketsOptions) {
  const monthBucketsRef = useRef<Map<string, ReservationsBucket>>(new Map());
  const activeBucketKeyRef = useRef<string>("");

  /**
   * Returns a bucket for the key, creating one if missing.
   */
  const getOrCreateBucket = useCallback((key: string) => {
    const existing = monthBucketsRef.current.get(key);
    if (existing) return existing;
    const fresh = createEmptyBucket();
    monthBucketsRef.current.set(key, fresh);
    return fresh;
  }, []);

  /**
   * Resets a bucket to empty and optionally clears Apollo cache.
   */
  const resetBucket = useCallback(
    (key: string, options?: { clearApollo?: boolean }) => {
      const fresh = createEmptyBucket();
      monthBucketsRef.current.set(key, fresh);
      if (options?.clearApollo) clearReservationsCache();
      if (activeBucketKeyRef.current === key) applyBucketState(fresh);
      return fresh;
    },
    [applyBucketState, clearReservationsCache],
  );

  /**
   * Updates a bucket via a mutating updater and syncs active state.
   */
  const updateBucket = useCallback(
    (
      key: string,
      updater: (bucket: ReservationsBucket) => ReservationsBucket,
    ) => {
      const current = getOrCreateBucket(key);
      const updated = updater(current);
      monthBucketsRef.current.set(key, updated);
      if (activeBucketKeyRef.current === key) applyBucketState(updated);
      return updated;
    },
    [applyBucketState, getOrCreateBucket],
  );

  /**
   * Switches the active bucket and applies its state.
   */
  const setActiveBucketKey = useCallback(
    (key: string) => {
      if (activeBucketKeyRef.current === key) return;
      activeBucketKeyRef.current = key;
      const bucket = getOrCreateBucket(key);
      applyBucketState(bucket);
    },
    [applyBucketState, getOrCreateBucket],
  );

  /**
   * Derives the current bucket key from applied search variables.
   */
  const bucketKey = useMemo(
    () => getBucketKey(appliedSearchVariables),
    [appliedSearchVariables],
  );

  useEffect(() => {
    setActiveBucketKey(bucketKey);
  }, [bucketKey, setActiveBucketKey]);

  return {
    activeBucketKeyRef,
    getOrCreateBucket,
    resetBucket,
    updateBucket,
    bucketKey,
  };
}
